<template>
    <dialog-window
        v-model="isOpen"
        max-width="450"
        :title="t('travelCard.travelCardDeleteTitle')"
        data-id="travelCardDeleteDialog"
        @close="handleCancel"
    >
        <v-form ref="form" @submit.prevent="handleSubmit">
            <form-section class-name="mb-2 px-3" :is-loading="false">
                <form-description :text="deleteFromDescription" />
                <v-card-actions class="d-flex justify-end ma-0 pa-0">
                    <card-button
                        class="flex-1-0-0"
                        size="large"
                        :inactive-label="t('form.cancel')"
                        data-id="travelCardDeleteFormCancelButton"
                        @click="handleCancel"
                    >
                        {{ t('form.cancel') }}
                    </card-button>
                    <card-button
                        type="submit"
                        class="flex-1-0-0"
                        size="large"
                        variant="flat"
                        color="error"
                        :inactive-label="t('form.removeCard')"
                        :loading="isSubmitting"
                        data-id="travelCardDeleteFormSubmitButton"
                    >
                        {{ t('form.removeCard') }}
                    </card-button>
                </v-card-actions>
            </form-section>
        </v-form>
    </dialog-window>
    <notification-bar
        v-model:show="notificationBarState.show"
        :color="notificationBarState.color"
        :icon="notificationBarState.icon"
        :border="notificationBarState.border"
        :message="notificationBarState.message"
    />
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { IFormProps } from '@/features/TravelCards/types';
import CardButton from '@/features/common/components/CardButton.vue';
import { FormDescription, FormSection, NotificationBar } from '@/features/common/components/form';
import { deleteTravelCard } from '../../../services/api';
import { useCardStore } from '@/features/TravelCards/store/cardStore';
import { useMainStore } from '@/features/common/stores';
import { useGlobalDialogStore } from '@/store/dialogStore';
import { useFormHandling, useNotification } from '@/features/common/components/form/composables';
import DialogWindow from '@/features/common/components/DialogWindow.vue';
import { splitTextPattern } from '@/helpers';

const props = defineProps<IFormProps & { type: string }>();
const emit = defineEmits(['submit-success', 'cancel']);

const { t } = useI18n();

const { form, isSubmitting, toggleIsSubmitting } = useFormHandling();
const { notificationBarState, notificationSuccess, notificationError } = useNotification();
const { isDialogOpen, closeDialog } = useGlobalDialogStore();
const mainStore = useMainStore();

const { deleteItem } = useCardStore();

const isOpen = computed(() => isDialogOpen(props.type));

const deleteFromDescription = computed(
    () =>
        `${deleteConfirmationText.value.before} "${props.travelCardName}" ${deleteConfirmationText.value.after} '${props.travelCardNumber}' ?`
);

const deleteConfirmationText = computed(() => splitTextPattern(t('travelCard.travelCardDeleteConfirmation'), '[CARD]'));

const saveData = async () => {
    try {
        await deleteTravelCard(props.travelCardId!);
        notificationBarState.value = notificationSuccess(t('travelCard.travelCardDeletedSuccess'));
        return true;
    } catch (error: any) {
        notificationBarState.value = notificationError(t('travelCard.travelCardDeletedFailed'));
        mainStore.registerError(error);
        return false;
    }
};

const submitForm = async () => {
    try {
        toggleIsSubmitting(true);
        if (!props.travelCardId) return;
        const result = await saveData();
        if (result) {
            deleteItem(props.travelCardId);
            emit('submit-success');
            closeDialog(props.type);
        }
    } finally {
        toggleIsSubmitting(false);
    }
};

const handleSubmit = async () => await submitForm();

const handleCancel = () => {
    emit('cancel');
    closeDialog(props.type);
};
</script>
